// @todo(surf): cascading 위해 상단에 import 하지만, 추후 @healingpaper/consultation-web 라이브러리 제거 시 제발 제거해주세요~
import '@healingpaper/consultation-web/build.css'
import 'react-modern-calendar-datepicker/lib/DatePicker.css'
import '@/app/globals.css'

import '@/utils/datadog/rum'
import { isPretendardAvailable, PretendardVariable } from '@unni/next-font'
import { DehydratedState, HydrationBoundary, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { theme } from '@healingpaper/cell'
import { I18nextProvider } from 'react-i18next'
import Cookies from 'js-cookie'
import Head from 'next/head'
import Router from 'next/router'
import progress from 'nprogress'
import { useEffect, useMemo, useRef } from 'react'
import { ThemeProvider } from 'styled-components'
import { TelemeterProvider } from '@healingpaper/shared'
import { AirbridgeDeviceAlias, airbridgeInstance } from '@unni/airbridge'
import Script from 'next/script'

import { cacheQuerystring, postAmplitude, postGtag, setGA4UserProperties } from '@/core/lib'
import { GlobalStateProvider } from '@/hooks/GlobalState'
import ModalProvider from '@/hooks/Modal/ModalProvider'
import NotificationProvider from '@/hooks/Notification/NotificationProvider'
import GlobalStyle from '@/theme/GlobalStyle'
import { initAirbridge, sendAirbridgeEvent, AirbridgeEvents } from '@/utils/airbridge'
import '@/utils/amplitude'
import createI18n from '@/utils/i18n/i18n-client'
import { type Countries, countryToLocale, Locales, localeToLanguage } from '@/utils/language'
import { amplitudeTracker } from '@/utils/amplitude'
import { FirstVisitProvider } from '@/hooks/FirstVisitContext'
import { logCookieStats, cleanupTempCookies } from '@/utils/cookie-utils'
import { SEOMeta } from '@/shared/utils'
import { CookieConsent } from '@/features/cookie-consent'
import { RecaptchaProvider } from '@/shared/libs/recaptcha/context'
import { AuthTokenTracker } from '@/features/oauth/utils/auth-token-tracker'
import channelTalkService from '@/shared/libs/channel-talk'
import { SigninPaywall } from '@/features/oauth/components'
import { getLoginSourceType, clearPaywallSession } from '@/utils/login-source'
import UnniGuideFab from '@/features/unni-guide/components/fab'

import type { AppProps } from 'next/app'
import type { User } from '@/features/member/member.type'

import '@/components/shared/ABTest/ABTest'

// 페이지 로딩 시 상단에 프로그레스 넣기
Router.events.on('routeChangeStart', () => progress.start())
Router.events.on('routeChangeComplete', () => progress.done())
Router.events.on('routeChangeError', () => progress.done())

export interface GlobalPageProps {
  user: User
  error?: any
  meta?: SEOMeta
  dehydratedState?: DehydratedState
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      retry: false,
    },
  },
})

const GangnamunniApp = (props: AppProps<GlobalPageProps>): JSX.Element => {
  const locale = countryToLocale(props.router.locale as Countries)
  const language = localeToLanguage(locale)
  const { Component, pageProps, router } = props
  const i18n = useMemo(() => createI18n({ locale }), [locale])

  // page 안의 getInitialProps 에서 Error 가 발생할 경우
  if (pageProps.error) {
    throw pageProps.error
  }
  useEffect(() => {
    const isSignin = Cookies.get('isSignin')
    const siginAuthType = Cookies.get('signInAuthType')

    if (pageProps.user?.id && isSignin === 'true' && siginAuthType) {
      // 로그인 성공 시 임시 쿠키들 정리
      cleanupTempCookies()

      const target = (siginAuthType.split('_')[1] ?? '').toLowerCase()
      const sourceType = getLoginSourceType()

      postGtag('login', {
        eventCategory: 'User',
        eventAction: 'Login',
        eventLabel: 'User Login',
        userId: pageProps.user.id,
      })

      // TODO: 아래 'login_success' 이벤트로 대체 후 삭제
      postAmplitude(`success_sign_in_${target}`, {
        path: router.asPath,
      })

      postAmplitude('login_success', {
        provider: target as 'kakao' | 'apple' | 'line' | 'facebook' | 'email',
        source_type: sourceType,
        path: router.asPath,
      })

      // Paywall 세션 정리
      clearPaywallSession()

      /**
       * airbridge 초기화 및 유저 정보 업데이트
       */
      sendAirbridgeEvent(AirbridgeEvents.USER_SIGN_IN, {
        semanticAttributes: {
          type: target.toUpperCase(),
        },
      })
    }
  }, [router.asPath, pageProps.user])

  useEffect(() => {
    cacheQuerystring(router.query)
  }, [router.query])

  // hashedToken 변경 추적을 위한 ref
  const prevHashedTokenRef = useRef<string | null>(null)

  useEffect(() => {
    /* User 정보가 변경 될 때 트래커 정보 업데이트 */
    // Amplitude 초기화
    const prevAmplitudeUserId = amplitudeTracker.getUserId()
    const hasFetchedUser = pageProps.user && pageProps.user?.id !== 0
    const noAmplitudeUserId = prevAmplitudeUserId === undefined
    const hasPrevAmplitudeUserId = !noAmplitudeUserId

    if (hasFetchedUser && noAmplitudeUserId) {
      amplitudeTracker.setUserId(`${pageProps.user?.id}`)
    }
    if (!hasFetchedUser && hasPrevAmplitudeUserId) {
      amplitudeTracker.setUserId(undefined)
    }

    // hashedToken 변경 시에만 User Property 업데이트 (중복 호출 방지)
    const currentHashedToken = AuthTokenTracker.getClientHashedToken()
    if (currentHashedToken && amplitudeTracker && currentHashedToken !== prevHashedTokenRef.current) {
      try {
        const identifyEvent = new amplitudeTracker.Identify()
        identifyEvent.set('hashed_token', currentHashedToken)
        amplitudeTracker.identify(identifyEvent)
        prevHashedTokenRef.current = currentHashedToken
        console.log('[AuthTokenTracker] hashedToken User Property updated in Amplitude')
      } catch (error) {
        console.warn('[AuthTokenTracker] Failed to update hashedToken in Amplitude:', error)
      }
    }

    airbridgeInstance.setDeviceAlias(AirbridgeDeviceAlias.Amplitude, amplitudeTracker.getDeviceId() || '')
  }, [pageProps.user])

  useEffect(() => {
    initAirbridge(pageProps.user)

    // 개발 환경에서 쿠키 크기 모니터링
    if (process.env.NODE_ENV === 'development') {
      logCookieStats()
    }
  }, [])

  useEffect(() => {
    /**
     * GA user email 수집
     * https://healingpaper.slack.com/archives/CD72QLB9T/p1723015956394769
     **/
    const user = pageProps.user
    if (user && user.id !== 0) {
      setGA4UserProperties(user.id, {
        email: user.loginId,
      })
    }
  }, [pageProps.user])

  // Channel Talk 초기화
  useEffect(() => {
    const initializeChannelTalk = async () => {
      // 모든 locale에서 Channel Talk 초기화 (국가별 workflow 지원)
      try {
        await channelTalkService.boot(pageProps.user, locale)
        console.log('Channel Talk initialized successfully for locale:', locale)
      } catch (error) {
        console.error('Failed to initialize Channel Talk:', error)
      }
    }

    // 브라우저 환경에서만 초기화
    if (typeof window !== 'undefined') {
      initializeChannelTalk()
    }

    // 컴포넌트 언마운트 시 정리
    return () => {
      if (typeof window !== 'undefined') {
        channelTalkService.shutdown()
      }
    }
  }, [pageProps.user, locale])

  return (
    <>
      <Head>
        <title>{pageProps.meta?.title || i18n.t('common.meta.title')}</title>
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=2, user-scalable=yes" />
      </Head>
      <Script src="/__ENV.js" strategy="beforeInteractive" />

      <GlobalStyle />
      <div className={isPretendardAvailable(language) ? PretendardVariable.className : ''}>
        <ThemeProvider theme={theme}>
          <FirstVisitProvider>
            <I18nextProvider i18n={i18n}>
              <QueryClientProvider client={queryClient}>
                <HydrationBoundary state={pageProps.dehydratedState || undefined}>
                  <GlobalStateProvider>
                    <TelemeterProvider
                      amplitude={{ logEvent: postAmplitude }}
                      airbridge={{ logEvent: sendAirbridgeEvent }}
                    >
                      <NotificationProvider>
                        <ModalProvider>
                          <RecaptchaProvider>
                            <main>
                              <Component {...pageProps} locale={locale} />
                            </main>
                            <div id="portal" />
                            {[Locales.EN_US, Locales.TH_TH].includes(locale) && <CookieConsent />}
                            <SigninPaywall isLoggedIn={pageProps.user?.id !== 0} />
                            <UnniGuideFab />
                          </RecaptchaProvider>
                        </ModalProvider>
                      </NotificationProvider>
                    </TelemeterProvider>
                  </GlobalStateProvider>
                </HydrationBoundary>
              </QueryClientProvider>
            </I18nextProvider>
          </FirstVisitProvider>
        </ThemeProvider>
      </div>
    </>
  )
}

export default GangnamunniApp
